import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import {
  InstanceResponseDto,
  ScenarioResponseDto,
  SnapshotResponseDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { SelectOption } from '@common-modules/shared/model/shared/select-option';
import { LogService } from '@common-modules/shared/wlm-log/log.service';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { asEnumerable } from 'linq-es2015';
import { ReplaySubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  MasterOperatorConnectItemsSettings,
  MasterOperatorConnectSettings,
} from '../models/master-operator-connect-settings';
import { BaseInstanceResponseDto } from '../models/master-operator-instance';
import { MasterOperatorType } from '../models/master-operator-type';
import { MasterOperatorService } from '../services/master-operator.service';

const isNotEmptyArrayValidator = (control: AbstractControl): { [key: string]: boolean } | null => {
  if (control.value && Array.isArray(control.value)) {
    return { 'is-empty-array': true };
  }
  return null;
};

const COMPONENT_SELECTOR = 'tc-master-operator-connect-form';

@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-connect-form.component.html',
    styleUrls: ['./master-operator-connect-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MasterOperatorConnectFormComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class MasterOperatorConnectFormComponent implements OnInit, ControlValueAccessor {
  @Input() masterOperatorType: MasterOperatorType;
  @Input() fieldAppearance = 'outline';
  @Input() masterInstanceIds: string[];

  form: UntypedFormGroup;
  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;
  readonly fieldClass = 'field-stretch';
  isAllDisabled: boolean;
  isInstanceDisabled = false;
  isSnapshotDisabled = true;
  isScenarioDisabled = true;
  private _instances: InstanceResponseDto[];

  instanceOptions: SelectOption<string>[];
  snapshotOptions: SelectOption<string>[];
  scenarioOptions: SelectOption<string>[];

  scenariosById: { [scenarioId: string]: ScenarioResponseDto } = {};
  snapshotsById: { [snapshotId: string]: SnapshotResponseDto } = {};
  instancesById: { [instanceId: string]: InstanceResponseDto } = {};
  instanceIdsBySnapshotId: { [snapshotId: string]: string } = {};

  private _dataLoaded$ = new ReplaySubject<void>(1);
  dataLoaded$ = this._dataLoaded$.asObservable();

  private _availableSnapshots: SnapshotResponseDto[];
  private _availableScenarios: ScenarioResponseDto[];

  private _currentInstances: InstanceResponseDto[];
  private _currentSnapshots: SnapshotResponseDto[];
  private _currentScenarios: ScenarioResponseDto[];
  private _previousResults = new Map<string, MasterOperatorConnectSettings>();

  private nativeOnChange: (value: MasterOperatorConnectItemsSettings) => void;
  private nativeOnTouched: (value: MasterOperatorConnectItemsSettings) => void;
  private setLoading: (loading: boolean) => void;
  private _dataLoadedResultsSubs: Subscription;

  constructor(
    private _masterOperatorService: MasterOperatorService,
    private _fb: UntypedFormBuilder,
    private _log: LogService,
    private _spinnerService: SpinnerService
  ) {
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  ngOnInit(): void {
    this.buildForm();
    this.buildOptions();
  }

  private buildForm(): void {
    this.form = this._fb.group({
      instanceIds: [[], [Validators.required, isNotEmptyArrayValidator]],
      snapshotIds: [[], [Validators.required, isNotEmptyArrayValidator]],
      scenarioIds: [[], [Validators.required, isNotEmptyArrayValidator]],
    });

    this.instancesCtrl.valueChanges.subscribe((instanceIds) => {
      this.setCurrentInstances(instanceIds);
    });

    this.snapshotsCtrl.valueChanges.subscribe((snapshotIds) => {
      this.setCurrentSnapshots(snapshotIds);
    });

    this.scenariosCtrl.valueChanges.subscribe((scenarioIds) => {
      this.setCurrentScenarios(scenarioIds);

      // In the end of the chain, we emit the output value.
      this._dataLoadedResultsSubs?.unsubscribe();
      this._dataLoadedResultsSubs = this.dataLoaded$.pipe(untilDestroyed(this)).subscribe(() => {
        this.emitResults();
      });
    });
  }

  private setCurrentInstances(instanceIds: string[], params = { avoidEmit: false }): void {
    this._currentInstances = this._instances?.filter((instance) =>
      instanceIds.find((id) => id === instance.instanceId)
    );

    this.buildSnapshotOptions(params);

    if (instanceIds.length) {
      this.isSnapshotDisabled = false;
      this.updateDisabledState();
    }
  }

  private setCurrentSnapshots(snapshotIds: string[], params = { avoidEmit: false }): void {
    this._currentSnapshots = this._availableSnapshots?.filter((snapshot) =>
      snapshotIds.find((id) => id === snapshot.snapshotId)
    );

    this._currentSnapshots = this._currentSnapshots ?? [];

    this.buildScenarioOptions(params);

    if (snapshotIds.length) {
      this.isScenarioDisabled = false;
      this.updateDisabledState();
    }
  }

  private setCurrentScenarios(scenarioIds: string[]): void {
    this._currentScenarios =
      this._availableScenarios?.filter((scenario) =>
        scenarioIds.find((id) => id === scenario.scenarioId)
      ) ?? [];
  }

  private buildOptions(): void {
    this.buildInstanceOptions();
  }

  private buildInstanceOptions(): void {
    this.setLoading(true);
    this._masterOperatorService
      .getInstances({ type: this.masterOperatorType })
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((activeInstances) => {
        let instances = activeInstances;
        if (this.masterInstanceIds?.length) {
          instances = activeInstances.filter((x) =>
            this.masterInstanceIds.find((id) => x.realTimeId == id)
          );
        }
        this._instances = instances;

        this.instanceOptions = this._instances.map((instance) => ({
          label: this._masterOperatorService.instanceReadableLabel(
            instance as BaseInstanceResponseDto
          ),
          value: instance.instanceId,
        }));

        this.updateInstanceHelpers();
        this._dataLoaded$.next();
      });
  }

  private buildSnapshotOptions({ avoidEmit } = { avoidEmit: false }): void {
    //if (this._currentInstances.length) {
    this._availableSnapshots = this.selectMany<SnapshotResponseDto>(
      this._currentInstances.map((instance) => instance.snapshots)
    );

    this.snapshotOptions = this._availableSnapshots.map((snapshot) => ({
      label: snapshot.name,
      value: snapshot.snapshotId,
    }));

    // Set default snapshots
    this._currentSnapshots = this._availableSnapshots.filter((snapshot) => snapshot.isDefault);

    if (!this._currentSnapshots) {
      this._log.warn({
        msg: `The instances ${this._currentInstances} does not have a default snapshot.`,
      });
    }

    this.updateSnapshotHelpers();

    this.snapshotsCtrl.setValue(
      this._currentSnapshots.map((snapshot) => snapshot.snapshotId),
      {
        emitEvent: !avoidEmit,
      }
    );
    //}
  }

  private buildScenarioOptions({ avoidEmit } = { avoidEmit: false }): void {
    //if (this._currentSnapshots.length) {
    this._availableScenarios = this.selectMany<ScenarioResponseDto>(
      this._currentSnapshots.map((snapshot) => snapshot.scenarios)
    );

    this.scenarioOptions = this._availableScenarios.map((scenario) => ({
      label: scenario.name,
      value: scenario.scenarioId,
    }));

    // Set default scenario
    this._currentScenarios = this._availableScenarios.filter((scenario) => scenario.isActive);

    if (!this._currentScenarios.length) {
      this._log.warn({
        msg: `The snapshot ${this._currentSnapshots} does not have an active scenario.`,
      });
    }

    this.updateScenarioHelpers();

    this.scenariosCtrl.setValue(
      this._currentScenarios.map((scenario) => scenario.scenarioId),
      { emitEvent: !avoidEmit }
    );
    //}
  }

  private updateInstanceHelpers(): void {
    this._instances.forEach((instance) => {
      if (!this.instancesById[instance.instanceId]) {
        this.instancesById[instance.instanceId] = instance;
      }
    });
  }

  private updateSnapshotHelpers(): void {
    this._availableSnapshots.forEach((snapshot) => {
      if (!this.snapshotsById[snapshot.snapshotId]) {
        const instance = this._currentInstances.find(
          (instance) => instance.instanceId === snapshot.instanceId
        );

        this.snapshotsById[snapshot.snapshotId] = snapshot;
        this.instanceIdsBySnapshotId[snapshot.snapshotId] = instance.instanceId;
      }
    });
  }

  private updateScenarioHelpers(): void {
    this._availableScenarios.forEach((scenario) => {
      if (!this.scenariosById[scenario.scenarioId]) {
        this.scenariosById[scenario.scenarioId] = scenario;
      }
    });
  }

  private emitResults(): void {
    const results: MasterOperatorConnectSettings[] = [];
    this._currentScenarios?.forEach((scenario) => {
      if (this._previousResults.has(scenario.scenarioId)) {
        const result = this._previousResults.get(scenario.scenarioId);
        results.push(result);
      } else {
        const result = this.buildConnectSettings(scenario);
        results.push(result);
        this._previousResults.set(scenario.scenarioId, result);
      }
    });

    const result: MasterOperatorConnectItemsSettings = {
      items: results,
      type: this.masterOperatorType,
      singleId: null,
    };

    this.nativeOnChange(result);
  }

  private buildConnectSettings(scenario: ScenarioResponseDto): MasterOperatorConnectSettings {
    const snapshot = this.snapshotsById[scenario.snapshotId];
    const instanceId = this.instanceIdsBySnapshotId[snapshot.snapshotId];
    const instance = this.instancesById[instanceId];

    let result = null;

    if (instance) {
      // Removing snapshotGroups from the copied props.
      const { snapshots, ...instanceProperties } = instance;

      result = {
        ...globalUtilsHelper.clone(instanceProperties, true),
        snapshotId: snapshot.snapshotId,
        snapshotName: snapshot.name,
        scenarioId: scenario.scenarioId,
        scenarioName: scenario.name,
        instanceStatus: instance.status,
        snapshotIsDefault: snapshot.isDefault,
        scenarioIsActive: scenario.isActive,
      };
    }
    return result;
  }

  /**
   * Receive the value that comes from the generic control.
   */

  writeValue(connectSettings: MasterOperatorConnectItemsSettings): void {
    if (connectSettings) {
      this.dataLoaded$.pipe(untilDestroyed(this)).subscribe((loaded) => {
        const instanceIdsSet = new Set<string>();
        const snapshotIdsSet = new Set<string>();
        const scenarioIdsSet = new Set<string>();

        connectSettings.items.forEach((item) => {
          instanceIdsSet.add(item.instanceId);
          snapshotIdsSet.add(item.snapshotId);
          scenarioIdsSet.add(item.scenarioId);
        });

        const instanceIds = Array.from(instanceIdsSet.values());
        const snapshotIds = Array.from(snapshotIdsSet.values());
        const scenarioIds = Array.from(scenarioIdsSet.values());

        const avoidEmit = { avoidEmit: true };
        this.instancesCtrl.setValue(instanceIds, { emitEvent: false });
        this.setCurrentInstances(instanceIds, avoidEmit);
        this.snapshotsCtrl.setValue(snapshotIds, { emitEvent: false });
        this.setCurrentSnapshots(snapshotIds, avoidEmit);
        this.scenariosCtrl.setValue(scenarioIds, { emitEvent: false });
        this.setCurrentScenarios(scenarioIds);
      });
    }
  }

  /**
   * Propagates the changes so that the binded form or ngModel can receive them.
   */
  registerOnChange(fn: any): void {
    this.nativeOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.nativeOnTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isAllDisabled = isDisabled;
    this.updateDisabledState();
  }

  private updateDisabledState(): void {
    this.toggleDisable(this.instancesCtrl, this.isAllDisabled || this.isInstanceDisabled);
    this.toggleDisable(this.snapshotsCtrl, this.isAllDisabled || this.isSnapshotDisabled);
    this.toggleDisable(this.scenariosCtrl, this.isAllDisabled || this.isScenarioDisabled);
  }

  private toggleDisable(ctrl: AbstractControl, disable: boolean): void {
    if (disable && !ctrl.disabled) {
      ctrl.disable();
    }
    if (!disable && ctrl.disabled) {
      ctrl.enable();
    }
  }

  get instancesCtrl(): AbstractControl {
    return this.form.get('instanceIds');
  }

  get snapshotsCtrl(): AbstractControl {
    return this.form.get('snapshotIds');
  }

  get scenariosCtrl(): AbstractControl {
    return this.form.get('scenarioIds');
  }

  private selectMany<T>(array: any[], selectorFn?: (item: T) => any): T[] {
    if (selectorFn) {
      return asEnumerable(array).SelectMany<T>().ToArray();
    }
    return asEnumerable(array).SelectMany<T>(selectorFn).ToArray();
  }
}
